import { Button } from '@askable/ui/core/button';
import { cn } from '@askable/ui/lib/utils';
import { X, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { NewBadge } from './NewBadge';

import type { FeaturedCallout as FeaturedCalloutType } from '../types';

type FeaturedCalloutProps = {
  item: FeaturedCalloutType;
  isCollapsed: boolean;
  onDismiss: () => void;
};

export const FeaturedCallout = ({ item, isCollapsed, onDismiss }: FeaturedCalloutProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        `absolute z-50 w-60 cursor-default overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md`,
        { 'bottom-2 left-full ml-1': isCollapsed, 'bottom-8 left-1/2 mb-2 -translate-x-1/2': !isCollapsed },
      )}
      aria-live="polite"
      onClick={e => e.stopPropagation()}
    >
      <div
        className={cn('relative h-10 w-full bg-secondary', { 'h-[136px]': item.img_path })}
        style={{ backgroundColor: item.color ?? undefined }}
      >
        <Button variant="ghost" size="icon" className="absolute right-0 top-0 shrink-0" onClick={onDismiss}>
          <X className="size-4" />
        </Button>
        {item.img_path ? <img src={item.img_path} alt={item.title} className="h-full w-full object-contain" /> : null}
      </div>
      <div className="flex flex-col gap-2 p-3">
        <header>
          <h3 className="text-pretty text-sm font-semibold leading-tight">
            {item.title} <NewBadge />
          </h3>
        </header>

        {item.description ? (
          <p className="line-clamp-3 text-pretty text-sm text-muted-foreground">{item.description}</p>
        ) : null}

        {item.link ? (
          <a
            className="flex items-center gap-1 no-underline hover:underline focus:underline"
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('sections.newFeatureNotifications.readMore')}
            <ArrowRight className="size-4" />
          </a>
        ) : null}
      </div>
    </div>
  );
};
